import React, { createContext, Dispatch, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import ReactLocalization, { GlobalStrings, LocalizedStrings } from 'react-localization';
import store from 'store2';
import type { AlternateLanguageOptions, PrimaryLanguageOptions } from '../API/APIObjects';
import { LocalStorageKeys } from '../CommonUtils/Storage';
import { default as enus } from '../Localization/en-us.json';
import { default as esus } from '../Localization/es-us.json';
import { default as frca } from '../Localization/fr-ca.json';
import type { LocalizeForLanguageHook } from './Localization.types';
import { Payload } from './Payload';
import { LocalizationProvider as MUIDateLocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

export enum Locales {
    en = 'en-US',
    es = 'es-US',
    fr = 'fr-CA',
    none = ''
}

export const LocalesFull: Record<Locales, string> = {
    [Locales.en]: 'English',
    [Locales.es]: 'Spanish',
    [Locales.fr]: 'French',
    [Locales.none]: 'None',
};

export type LocalizationDefinitions = {
    [key in keyof typeof enus]: string;
};

const localeMap: GlobalStrings<LocalizationDefinitions> = {
    [Locales.en]: enus,
    /*
     * If a key is not defined, the ReactLocalization falls back on
     * the value defined in the above object so it's fine if certain
     * keys are missing from those below.
     */
    [Locales.es]: esus as LocalizationDefinitions,
    [Locales.fr]: frca as LocalizationDefinitions,
};

export enum LocalizationTypes {
    SetLocale = 'SET_LOCALE'
}

type LocalizationPayload = {
    [LocalizationTypes.SetLocale]: Locales;
};

type LocalizationActions = Payload<LocalizationPayload>[keyof Payload<LocalizationPayload>];

type LocalizationStateType = {
    Localization: LocalizedStrings<LocalizationDefinitions>;
    locale: Locales;
};

const LocalizationReducer = (state: LocalizationStateType, action: LocalizationActions): LocalizationStateType => {
    switch (action.type) {
        case LocalizationTypes.SetLocale:
            state.Localization.setLanguage(action.payload);
            dayjs.locale(action.payload === Locales.fr ? 'fr' : 'en');

            return { ...state,
                locale: action.payload };
        default:
            return state;
    }
};

const initialState: LocalizationStateType = {
    Localization: new ReactLocalization<LocalizationDefinitions>(localeMap),
    locale: Locales.en,
};
const LocalizationStateContext = createContext<LocalizationStateType>(initialState);
const LocalizationDispatchContext = createContext<Dispatch<LocalizationActions>>(() => null);

export const LocalizationProvider: React.FC<PropsWithChildren> = (props) => {
    const [state, dispatch] = useReducer(LocalizationReducer, initialState);

    useEffect(() => {
        let lang = store.local.get(LocalStorageKeys.BrowserLang);

        if (!lang) {
            if (navigator.languages) {
                for (const potentialLang of navigator.languages) {
                    if (potentialLang.toLocaleLowerCase()
                        .includes('fr') || potentialLang.toLocaleLowerCase()
                        .includes('en')) {
                        lang = potentialLang.toLocaleLowerCase();
                        break;
                    }
                }
            }

            if (!lang) {
                lang = navigator.language.toLocaleLowerCase();
            }

            store.local.set(LocalStorageKeys.BrowserLang, lang);
        }

        if (lang.includes('fr')) {
            dispatch({ type: LocalizationTypes.SetLocale,
                payload: Locales.fr });
        }
        else {
            dispatch({ type: LocalizationTypes.SetLocale,
                payload: Locales.en });
        }
    }, []);

    return (
        <LocalizationStateContext.Provider value={state}>
            <LocalizationDispatchContext.Provider value={dispatch}>
                <MUIDateLocalizationProvider adapterLocale={state.locale === Locales.fr ? 'fr' : 'en'} dateAdapter={AdapterDayjs}>
                    {props.children}
                </MUIDateLocalizationProvider>
            </LocalizationDispatchContext.Provider>
        </LocalizationStateContext.Provider>
    );
};

export const useLocale = () => {
    const context = useContext(LocalizationStateContext);
    if (context === undefined) {
        throw new Error('No Localization Context');
    }

    return context.Localization.getLanguage() as Locales;
};

export const useLocalization = () => {
    const context = useContext(LocalizationStateContext);
    if (context === undefined) {
        throw new Error('No Localization Context');
    }

    return context.Localization;
};

/**
 * Returns a helper that localizes a single key in a given dealer language code,
 * falling back to English. Omit the language to use the current interface language.
 */
export const useLocalizeForLanguage: LocalizeForLanguageHook = () => {
    const Localization = useLocalization();

    return useCallback((key: keyof LocalizationDefinitions, language?: PrimaryLanguageOptions | AlternateLanguageOptions): string => {
        // Dealer language codes (e.g. 'en-us') map to the Locales key the files are registered under (e.g. 'en-US').
        const locale: Locales | undefined = language ? {
            'en-us': Locales.en,
            'es-us': Locales.es,
            'fr-ca': Locales.fr,
        }[language] : undefined;

        return Localization.getString(key, locale) ?? Localization.getString(key, Locales.en) ?? '';
    }, [Localization]);
};

export const useLocalizationDispatch = () => {
    const context = useContext(LocalizationDispatchContext);
    if (context === undefined) {
        throw new Error('No Localization Dispatch Context');
    }

    return context;
};

export const useLocalizationHelpers = () => {
    const Localization = useLocalization();

    return useMemo(() => {
        return {
            /**
             * Returns a localized message stating the field is required
             */
            formatRequired: (field: string) => Localization.formatString(Localization.FieldIsRequired, field) as string,
            /**
             * Returns a localized message stating the field is required based on some other conditional requirement
             */
            formatRequiredFor: (field: string, requirement: string) => Localization.formatString(Localization.FieldIsRequiredFor_IGNORE, field, requirement) as string,
            /**
             * Returns a localized message stating the field is invalid
             */
            formatInvalid: (field: string) => Localization.formatString(Localization.FieldIsInvalid, field) as string,
        };
    }, [Localization]);
};

export const useLocalizeDayOfWeekString = () => {
    const Localization = useLocalization();

    return useCallback((dayOfWeekString: string) => {
        return dayOfWeekString ? dayOfWeekString
            .replace(/{MON}/, Localization.DayAbrMON)
            .replace(/{TUE}/, Localization.DayAbrTUE)
            .replace(/{WED}/, Localization.DayAbrWED)
            .replace(/{THU}/, Localization.DayAbrTHU)
            .replace(/{FRI}/, Localization.DayAbrFRI)
            .replace(/{SAT}/, Localization.DayAbrSAT)
            .replace(/{SUN}/, Localization.DayAbrSUN) : '';
    }, [Localization]);
};


export const useLocalizeTimeTakenString = () => {
    const Localization = useLocalization();
    return useCallback((timeTaken: string) => {
        return timeTaken ? timeTaken
            .replace(/{m}/, Localization.ShortAbbreviationMinute)
            .replace(/{s}/, Localization.ShortAbbreviationSecond) : '';
    }, [Localization]);
};